import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  obtenerCuentaActual,
  obtenerPerfilTutorLocal,
  guardarPerfilTutorLocal,
  type GuardarPerfilTutorRequest,
} from "../api";
import Campo from "../componentes/Campo";

export default function MiPerfil() {
  const qc = useQueryClient();
  const cuenta = useQuery({ queryKey: ["cuenta"], queryFn: obtenerCuentaActual });

  const [nombres, setNombres] = useState("");
  const [apellidos, setApellidos] = useState("");
  const [rut, setRut] = useState("");
  const [telefono, setTelefono] = useState("");
  const [telefonoEmergencia, setTelefonoEmergencia] = useState("");
  const [direccion, setDireccion] = useState("");
  const [comuna, setComuna] = useState("");
  const [notasContacto, setNotasContacto] = useState("");

  const [guardadoExitoso, setGuardadoExitoso] = useState(false);
  const [errores, setErrores] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (cuenta.data) {
      const p = obtenerPerfilTutorLocal(
        cuenta.data.usuarioID,
        cuenta.data.nombre,
        cuenta.data.email,
      );
      setNombres(p.nombres);
      setApellidos(p.apellidos ?? "");
      setRut(p.rut ?? "");
      setTelefono(p.telefono ?? "");
      setTelefonoEmergencia(p.telefonoEmergencia ?? "");
      setDireccion(p.direccion ?? "");
      setComuna(p.comuna ?? "");
      setNotasContacto(p.notasContacto ?? "");
    }
  }, [cuenta.data]);

  if (cuenta.isPending) return <p className="muted">Cargando perfil…</p>;
  if (cuenta.error) return <p className="err">{cuenta.error.message}</p>;
  if (!cuenta.data) return null;

  const c = cuenta.data;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrores({});
    setGuardadoExitoso(false);

    if (!nombres.trim()) {
      setErrores({ nombres: ["El nombre es obligatorio."] });
      return;
    }

    const req: GuardarPerfilTutorRequest = {
      nombres: nombres.trim(),
      apellidos: apellidos.trim() || null,
      rut: rut.trim() || null,
      telefono: telefono.trim() || null,
      telefonoEmergencia: telefonoEmergencia.trim() || null,
      direccion: direccion.trim() || null,
      comuna: comuna.trim() || null,
      notasContacto: notasContacto.trim() || null,
    };

    guardarPerfilTutorLocal(c.usuarioID, req, c.email);
    setGuardadoExitoso(true);
    qc.invalidateQueries({ queryKey: ["tutor-perfil", c.usuarioID] });

    setTimeout(() => {
      setGuardadoExitoso(false);
    }, 4000);
  };

  return (
    <>
      <p className="migas">
        <Link to="/">← Mis mascotas</Link>
      </p>

      <div className="titulo-fila">
        <div>
          <h1>Mi Perfil de Tutor</h1>
          <p className="muted" style={{ margin: 0, fontSize: "0.9rem" }}>
            Gestiona tus datos de contacto para que el veterinario pueda comunicarse contigo ante cualquier necesidad clínica.
          </p>
        </div>
      </div>

      <div className="perfil-layout" style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1.5rem", marginTop: "1rem" }}>
        {/* Tarjeta de Cuenta */}
        <section className="panel" style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              backgroundColor: "var(--accent)",
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              fontWeight: "bold",
            }}
          >
            {nombres ? nombres.charAt(0).toUpperCase() : "👤"}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: "1.2rem" }}>
              {[nombres, apellidos].filter(Boolean).join(" ") || c.nombre}
            </h2>
            <p className="muted" style={{ margin: "0.15rem 0 0", fontSize: "0.85rem" }}>
              📧 {c.email} · Rol: <span className="etiqueta neutra">{c.rolGlobal}</span>
            </p>
          </div>
        </section>

        {/* Formulario de Datos del Tutor */}
        <form className="panel formulario" onSubmit={handleSubmit}>
          <h2>Datos de Contacto y Emergencia</h2>

          {guardadoExitoso && (
            <div className="aviso-exito" style={{ padding: "0.75rem 1rem", backgroundColor: "var(--exito-suave)", border: "1px solid var(--exito)", borderRadius: "0.5rem", color: "var(--ink)" }}>
              ✅ <strong>¡Datos guardados con éxito!</strong> Tu información de tutor se encuentra actualizada.
            </div>
          )}

          <div className="fila">
            <Campo etiqueta="Nombres" errores={errores.nombres} requerido>
              <input
                type="text"
                value={nombres}
                onChange={(e) => setNombres(e.target.value)}
                placeholder="Ej: Carolina"
                required
              />
            </Campo>

            <Campo etiqueta="Apellidos" errores={errores.apellidos}>
              <input
                type="text"
                value={apellidos}
                onChange={(e) => setApellidos(e.target.value)}
                placeholder="Ej: Silva Rojas"
              />
            </Campo>
          </div>

          <div className="fila">
            <Campo
              etiqueta="RUT / DNI"
              errores={errores.rut}
              ayuda="Para recetas médicas o certificados oficiales"
            >
              <input
                type="text"
                value={rut}
                onChange={(e) => setRut(e.target.value)}
                placeholder="Ej: 12.345.678-9"
              />
            </Campo>

            <Campo
              etiqueta="Teléfono principal"
              errores={errores.telefono}
              ayuda="Con código de país (ej: +56 9 1234 5678)"
            >
              <input
                type="tel"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                placeholder="+56 9 1234 5678"
              />
            </Campo>
          </div>

          <div className="fila">
            <Campo
              etiqueta="Teléfono de emergencia / 2° tutor"
              errores={errores.telefonoEmergencia}
              ayuda="Número alternativo en caso de no contestar"
            >
              <input
                type="tel"
                value={telefonoEmergencia}
                onChange={(e) => setTelefonoEmergencia(e.target.value)}
                placeholder="+56 9 8765 4321"
              />
            </Campo>

            <Campo etiqueta="Comuna / Ciudad" errores={errores.comuna}>
              <input
                type="text"
                value={comuna}
                onChange={(e) => setComuna(e.target.value)}
                placeholder="Ej: Providencia, Santiago"
              />
            </Campo>
          </div>

          <Campo etiqueta="Dirección particular" errores={errores.direccion}>
            <input
              type="text"
              value={direccion}
              onChange={(e) => setDireccion(e.target.value)}
              placeholder="Ej: Av. Los Leones 1420, Depto 402"
            />
          </Campo>

          <Campo
            etiqueta="Notas o indicaciones de contacto"
            errores={errores.notasContacto}
            ayuda="Horarios preferidos de llamada, segundo cuidador, etc."
          >
            <textarea
              rows={2}
              value={notasContacto}
              onChange={(e) => setNotasContacto(e.target.value)}
              placeholder="Ej: Llamar por WhatsApp en horario laboral o contactar a mi pareja (Juan)."
            />
          </Campo>

          <div className="acciones" style={{ marginTop: "1rem" }}>
            <button type="submit" className="boton-primario">
              Guardar datos de tutor
            </button>
            <Link className="boton" to="/">
              Volver
            </Link>
          </div>
        </form>

        {/* Panel de ayuda y recomendaciones clínicas */}
        <section className="panel" style={{ backgroundColor: "var(--sunken)" }}>
          <h3 style={{ margin: "0 0 0.5rem 0", fontSize: "1rem" }}>💡 ¿Por qué es importante tener tus datos al día?</h3>
          <ul style={{ margin: 0, paddingLeft: "1.2rem", fontSize: "0.875rem", color: "var(--ink-2)" }}>
            <li>Permite al veterinario comunicarse de urgencia si tu mascota queda hospitalizada.</li>
            <li>Facilita la emisión de recetas médicas oficiales y certificados de viaje/salud con tu RUT y dirección.</li>
            <li>Si compartes la ficha mediante código QR o placa, quien la escanee podrá contactarte rápidamente.</li>
          </ul>
        </section>
      </div>
    </>
  );
}
